import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Link as LinkIcon,
  Search,
  ContentCopy,
  Refresh
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getLinkHistory } from '../services/linkHistoryService';

function LinkHistory() {
  const [links, setLinks] = useState([]);
  const [filteredLinks, setFilteredLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadLinks = async () => {
    setLoading(true);
    setError(null);

    try {
      // console.log('Carregando histórico de links...');
      const data = await getLinkHistory();
      // console.log('Dados recebidos do histórico:', data);
      setLinks(data);
      setFilteredLinks(data);
    } catch (err) {
      console.error('Erro ao carregar histórico de links:', err);
      setError('Erro ao carregar histórico de links. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLinks();
  }, []);

  useEffect(() => {
    const filtered = links.filter((link) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (link.courseName || '').toLowerCase().includes(searchLower) ||
        (link.userId || '').toLowerCase().includes(searchLower) ||
        (link.email || '').toLowerCase().includes(searchLower) ||
        (link.link || '').toLowerCase().includes(searchLower)
      );
    });
    setFilteredLinks(filtered);
    setPage(0);
  }, [searchTerm, links]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      // You could add a notification here if desired
    } catch (error) {
      console.error('Erro ao copiar link:', error);
      alert('Erro ao copiar link. Tente selecionar e copiar manualmente.');
    }
  };

  const formatDate = (timestamp) => {
    try {
      if (!timestamp) return 'N/A';
      if (timestamp.toDate) {
        return format(timestamp.toDate(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
      }
      if (typeof timestamp === 'string') {
        return format(new Date(timestamp), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
      }
      return 'N/A';
    } catch (error) {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadLinks} sx={{ ml: 2 }}>
          Tentar Novamente
        </Button>
      </Alert>
    );
  }

  const paginatedLinks = filteredLinks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Histórico de Links Gerados
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadLinks}
        >
          Atualizar
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Buscar por projeto, usuário, email ou link..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <LinkIcon color="action" />
          <Typography variant="body2" color="text.secondary">
            Mostrando {filteredLinks.length} de {links.length} links gerados
          </Typography>
        </Box>

        {filteredLinks.length === 0 ? (
          <Alert severity="info">
            {searchTerm ? 'Nenhum link encontrado com os critérios de busca.' : 'Nenhum link gerado ainda.'}
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Projeto</TableCell>
                    <TableCell>Usuário</TableCell>
                    <TableCell>SCORM</TableCell>
                    <TableCell>Link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLinks.map((link) => (
                    <TableRow key={link.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(link.createdAt || link.generatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={link.courseName || 'Sem projeto'}
                          size="small"
                          color={link.courseName ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {link.userId || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={link.isScorm ? 'Sim' : 'Não'}
                          size="small"
                          color={link.isScorm ? 'primary' : 'default'}
                          variant={link.isScorm ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mr: 1
                            }}
                            title={link.link}
                          >
                            {link.link}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyLink(link.link)}
                            title="Copiar link"
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={filteredLinks.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Informações sobre o Histórico de Links
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Como funciona?
                </Typography>
                <Typography variant="body2" paragraph>
                  Esta aba mostra todos os links gerados através do sistema de avaliação.
                </Typography>
                <Typography variant="body2" paragraph>
                  Cada link contém informações sobre o projeto, usuário e email associados.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Campos exibidos
                </Typography>
                <Typography variant="body2" paragraph>
                  • <strong>Data:</strong> Quando o link foi gerado
                </Typography>
                <Typography variant="body2" paragraph>
                  • <strong>Projeto:</strong> Nome do projeto associado
                </Typography>
                <Typography variant="body2" paragraph>
                  • <strong>Usuário:</strong> ID do usuário associado
                </Typography>
                <Typography variant="body2" paragraph>
                  • <strong>SCORM:</strong> Se a avaliação é para pacote SCORM
                </Typography>
                <Typography variant="body2">
                  • <strong>Link:</strong> O link gerado que pode ser copiado
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default LinkHistory;